import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Box,
    Card,
    CardContent,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
    Alert,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ruRU } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs'; // Подключаем dayjs для установки дефолтных дат
import 'dayjs/locale/ru';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import { get } from '../hooks/apiFetch';
import { useAuth } from '../Auth/AuthContext';

// --- КОНФИГУРАЦИЯ URL ---
const API_URLS = {
    total: '/api/stats/total',
    monthly: '/api/stats/monthly',
    daily: '/api/stats/daily',
    byUtility: '/api/stats/by-utility',
    getAllOrg: '/api/stats/getallorg',
    getUserOrg: '/api/stats/getuserorg'
};

const StatisticsPage = () => {
    // Состояния данных
    const [stats, setStats] = useState({ total: 0, monthly: 0, daily: [], byUtility: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { checkPermission } = useAuth();

    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [orgsLoading, setOrgsLoading] = useState(true);

    const [chartMode, setChartMode] = useState('total');
    const [utilityChartMode, setUtilityChartMode] = useState('tasks');
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month'));
    const [endDate, setEndDate] = useState(dayjs());

    const [hideEmptyDays, setHideEmptyDays] = useState(false);

    const UTILITY_COLORS = [
        '#2196f3', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63',
        '#4caf50', '#ffc107', '#795548', '#607d8b', '#f44336',
    ];

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                setOrgsLoading(true);
                let orgs;
                if (checkPermission("statistics_access")) {
                    const orgData = await get(API_URLS.getAllOrg);
                    orgs = Array.isArray(orgData) ? orgData : (orgData?.data || orgData?.organizations || []);
                } else {
                    orgs = await get(API_URLS.getUserOrg);
                }
                setOrganizations(orgs);
            } catch (err) {
                console.error('Failed to load organizations:', err);
            } finally {
                setOrgsLoading(false);
            }
        };

        fetchOrganizations();
    }, []);

    useEffect(() => {
        if (organizations.length > 0 && !selectedOrg) {
            if (checkPermission("statistics_access")) {
                setSelectedOrg('all');
            } else {
                setSelectedOrg(organizations[0]);
            }
        }
    }, [organizations, checkPermission, selectedOrg]);

    const fetchStats = useCallback(async (orgId) => {
        try {
            setLoading(true);
            setError(null);

            const queryParams = orgId && orgId !== 'all' ? `?orgId=${orgId}` : '';

            const [totalData, monthlyData, dailyData, byUtilityData] = await Promise.all([
                get(`${API_URLS.total}${queryParams}`),
                get(`${API_URLS.monthly}${queryParams}`),
                get(`${API_URLS.daily}${queryParams}`),
                get(`${API_URLS.byUtility}${queryParams}`),
            ]);

            const formattedDailyData = dailyData.map(item => ({
                ...item,
                displayDate: new Date(item.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
            }));

            const formattedUtilityData = byUtilityData.map(item => ({
                ...item,
                displayMonth: new Date(item.month).toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' })
            }));

            setStats({
                total: totalData.count || 0,
                monthly: monthlyData.count || 0,
                daily: formattedDailyData,
                byUtility: formattedUtilityData
            });
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Не удалось загрузить статистику. Проверьте подключение или API.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats(selectedOrg);
    }, [fetchStats, selectedOrg]);

    const handleOrgChange = (event) => {
        setSelectedOrg(event.target.value);
        setSelectedMonth('all');
    };

    const handleChartModeChange = (event, newMode) => {
        if (newMode !== null) {
            setChartMode(newMode);
        }
    };

    const handleUtilityChartModeChange = (event, newMode) => {
        if (newMode !== null) setUtilityChartMode(newMode);
    };

    const formatNumber = (num) => new Intl.NumberFormat('ru-RU').format(num);
    const formatDuration = (seconds) => `${seconds.toFixed(1)} сек`;

    const normalizeDate = (dateValue) => {
        const d = new Date(dateValue);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    // ИЗМЕНЕНИЕ 3: Добавлена логика скрытия пустых дней
    const filteredDailyData = stats.daily.filter(item => {
        if (!item.date) return true;

        const itemDate = normalizeDate(item.date);

        if (startDate && itemDate < startDate.format('YYYY-MM-DD')) return false;
        if (endDate && itemDate > endDate.format('YYYY-MM-DD')) return false;

        // Скрываем дни, где total равно 0 (или отсутствует)
        if (hideEmptyDays && (!item.total || item.total === 0)) return false;

        return true;
    });

    const resetDateFilter = () => {
        setStartDate(null);
        setEndDate(null);
        setHideEmptyDays(false); // Сбрасываем и галочку при сбросе фильтров
    };

    const uniqueMonths = [...new Set(stats.byUtility.map(item => item.displayMonth))];

    const filteredUtilityData = selectedMonth === 'all'
        ? stats.byUtility
        : stats.byUtility.filter(item => item.displayMonth === selectedMonth);

    const utilityBarData = filteredUtilityData.reduce((acc, item) => {
        const existing = acc.find(d => d.utilityType === item.utilityType);
        if (existing) {
            existing.total += item.total;
            existing.p95_duration = Math.max(existing.p95_duration, item.p95_duration);
        } else {
            acc.push({
                utilityType: item.utilityType,
                total: item.total,
                p95_duration: item.p95_duration
            });
        }
        return acc;
    }, []);

    const utilityLineData = stats.byUtility.reduce((acc, item) => {
        const existing = acc.find(d => d.displayMonth === item.displayMonth);
        if (existing) {
            existing[`${item.utilityType}_p95`] = item.durations;
            existing[`${item.utilityType}_total`] = item.total;
        } else {
            acc.push({
                displayMonth: item.displayMonth,
                [`${item.utilityType}_p95`]: item.durations,
                [`${item.utilityType}_total`]: item.total
            });
        }
        return acc;
    }, []);

    const uniqueUtilities = [...new Set(stats.byUtility.map(item => item.utilityType))];

    const getShortUtilityName = (fullName) => {
        if (!fullName) return '';
        const words = fullName.split(' ');

        if (fullName.length > 25) {
            if (words.length >= 3) {
                return words.slice(0, 2).join(' ') + '...';
            }
            return fullName.substring(0, 25) + '...';
        }

        return fullName;
    };

    if (loading && orgsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Статистика обработок
            </Typography>

            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel id="organization-select-label">Организация</InputLabel>
                    <Select
                        labelId="organization-select-label"
                        value={selectedOrg}
                        label="Организация"
                        onChange={handleOrgChange}
                        disabled={orgsLoading}
                    >
                        {checkPermission("statistics_access") && (
                            <MenuItem value="all">Все организации</MenuItem>
                        )}

                        {[...new Set(organizations.filter(org => org && org !== 'all'))].map((org, index) => (
                            <MenuItem key={`${org}-${index}`} value={org}>
                                {org}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            {/* Карточки с общими показателями */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                <Card sx={{ flex: 1, minWidth: '250px', boxShadow: 3 }}>
                    <CardContent>
                        <Typography color="textSecondary" gutterBottom variant="h6" className="standard-text">
                            Всего обработано
                        </Typography>
                        <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'var(--DT1520-Green) !important' }}>
                            {formatNumber(stats.total)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className="standard-text">
                            {selectedOrg === 'all' ? 'за все время' : 'для выбранной организации'}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ flex: 1, minWidth: '250px', boxShadow: 3 }}>
                    <CardContent>
                        <Typography color="textSecondary" gutterBottom variant="h6" className="standard-text">
                            Обработано за месяц
                        </Typography>
                        <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'var(--DT1520-Green) !important' }}>
                            {formatNumber(stats.monthly)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className="standard-text">
                            {selectedOrg === 'all' ? 'за текущий месяц' : 'для выбранной организации'}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Секция графика */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" component="h2">
                        Динамика обработок по дням
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale="ru"
                            localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
                        >
                            <DateTimePicker
                                className="datetime-picker"
                                label="Дата с"
                                value={startDate}
                                onChange={(v) => setStartDate(v && v.isValid() ? v : null)}
                                format="DD.MM.YYYY"
                                views={['year', 'month', 'day']}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <DateTimePicker
                                className="datetime-picker"
                                label="Дата по"
                                value={endDate}
                                onChange={(v) => setEndDate(v && v.isValid() ? v : null)}
                                format="DD.MM.YYYY"
                                views={['year', 'month', 'day']}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                        </LocalizationProvider>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hideEmptyDays}
                                    onChange={(e) => setHideEmptyDays(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={<span className="standard-text" style={{ fontSize: '0.875rem' }}>Скрыть дни без задач</span>}
                        />

                        {(startDate || endDate || hideEmptyDays) && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={resetDateFilter}
                            >
                                Сбросить
                            </Button>
                        )}

                        <ToggleButtonGroup
                            value={chartMode}
                            exclusive
                            onChange={handleChartModeChange}
                            aria-label="Режим отображения графика"
                            size="small"
                        >
                            <ToggleButton value="total" aria-label="Общие обработки" className="standard-text">
                                Общие
                            </ToggleButton>
                            <ToggleButton value="detailed" aria-label="Успешные и неуспешные" className="standard-text">
                                Успешные / Неуспешные
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                <Box sx={{ width: '100%', height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={filteredDailyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="displayDate" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'var(--DT1520-White) !important',
                                    border: `1px solid`
                                }}
                                formatter={(value) => [formatNumber(value), '']}
                            />
                            <Legend
                                formatter={(value) => <span className="standard-text">{value}</span>}
                            />

                            {chartMode === 'total' ? (
                                <Line
                                    type="monotone"
                                    dataKey="total"
                                    name="Всего обработок"
                                    strokeWidth={3}
                                    activeDot={{ r: 6 }}
                                />
                            ) : (
                                <>
                                    <Line
                                        type="monotone"
                                        dataKey="successful"
                                        name="Успешные"
                                        strokeWidth={3}
                                        stroke="var(--DT1520-Green)"
                                        activeDot={{ r: 6 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="unsuccessful"
                                        name="Неуспешные"
                                        strokeWidth={3}
                                        stroke="var(--DT1520-Red)"
                                        activeDot={{ r: 6 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="cancelled"
                                        name="Отменённые"
                                        strokeWidth={3}
                                        stroke="var(--DT1520-Purple)"
                                        activeDot={{ r: 6 }}
                                    />
                                </>
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>

                {(startDate || endDate) && (
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Показаны данные за период:{' '}
                            <strong>
                                {startDate ? startDate.format('DD.MM.YYYY') : 'начало'}
                                {' — '}
                                {endDate ? endDate.format('DD.MM.YYYY') : 'сегодня'}
                            </strong>
                            {' '}включительно.
                            {hideEmptyDays && ' (Дни без задач скрыты)'}
                        </Typography>
                    </Box>
                )}
            </Paper>

            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" component="h2">
                        Статистика по утилитам
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Месяц</InputLabel>
                            <Select
                                value={selectedMonth}
                                label="Месяц"
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                                <MenuItem value="all">Все месяцы</MenuItem>
                                {uniqueMonths.map(month => (
                                    <MenuItem key={month} value={month}>{month}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <ToggleButtonGroup
                            value={utilityChartMode}
                            exclusive
                            onChange={handleUtilityChartModeChange}
                            aria-label="Режим отображения по утилитам"
                            size="small"
                        >
                            <ToggleButton value="tasks" className="standard-text">
                                Количество задач
                            </ToggleButton>
                            <ToggleButton value="duration" className="standard-text">
                                P95 длительность
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                <Box sx={{ width: '100%', height: 450 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {utilityChartMode === 'tasks' ? (
                            <BarChart data={utilityBarData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="utilityType"
                                    tickFormatter={(value) => getShortUtilityName(value)}
                                    angle={-60}
                                    textAnchor="end"
                                    height={100}
                                    interval={0}
                                    tick={{ fontSize: 11 }}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    label={{ value: 'Количество задач', angle: -90, position: 'insideLeft' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'var(--DT1520-White) !important',
                                        border: `1px solid`,
                                        maxWidth: '300px',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                    }}
                                    formatter={(value) => [formatNumber(value), 'Задач']}
                                    labelFormatter={(label) => label}
                                />
                                <Legend
                                    formatter={(value) => <span className="standard-text">{value}</span>}
                                />
                                <Bar
                                    dataKey="total"
                                    name="Всего задач"
                                    fill="var(--DT1520-Green)"
                                    radius={[8, 8, 0, 0]}
                                />
                            </BarChart>
                        ) : (
                            <LineChart data={utilityLineData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="displayMonth"
                                    label={{ value: 'Месяц', position: 'insideBottom', offset: -5 }}
                                />
                                <YAxis
                                    label={{ value: 'P95 (сек)', angle: -90, position: 'insideLeft' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'var(--DT1520-White) !important',
                                        border: `1px solid`
                                    }}
                                    formatter={(value, name) => {
                                        const utilityName = name.replace('_p95', '');
                                        return [formatDuration(value), getShortUtilityName(utilityName)];
                                    }}
                                />
                                <Legend
                                    formatter={(value) => {
                                        const utilityName = value.replace('_p95', '');
                                        return <span className="standard-text">{getShortUtilityName(utilityName)}</span>;
                                    }}
                                />
                                {uniqueUtilities.map((utility, index) => {
                                    const color = UTILITY_COLORS[index % UTILITY_COLORS.length];
                                    return (
                                        <Line
                                            key={utility}
                                            type="monotone"
                                            dataKey={`${utility}_p95`}
                                            name={utility}
                                            stroke={color}
                                            strokeWidth={3}
                                            dot={{ r: 5, fill: color }}
                                            activeDot={{ r: 7 }}
                                        />
                                    );
                                })}
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                </Box>

                <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                        <strong>P95 длительность</strong> — 95-й перцентиль времени обработки.
                        Показывает, что 95% задач выполняются быстрее указанного значения.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default StatisticsPage;